import { create } from '@bufbuild/protobuf'
import { useMutation } from '@connectrpc/connect-query'
import * as React from 'react'
import { toast } from 'sonner'

import { renderEmailTemplate } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { RenderEmailTemplateRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import type { EmailTemplate } from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { captureError } from '@/lib/sentry'
import { isHtmlEmpty } from './email-compose-state'

interface UseEmailTemplateInsertionParams {
  organizationId: string
  inquiryId?: string
  contactId?: string
  parentType: ParentTypeEnum
  parentId: string
  getSubject: () => string
  getBody: () => string
  setSubject: (subject: string) => void
  replaceBody: (body: string) => void
  appendBody: (body: string) => void
  markEdited: () => void
}

export function useEmailTemplateInsertion({
  organizationId,
  inquiryId,
  contactId,
  parentType,
  parentId,
  getSubject,
  getBody,
  setSubject,
  replaceBody,
  appendBody,
  markEdited,
}: UseEmailTemplateInsertionParams) {
  const mutation = useMutation(renderEmailTemplate)

  const handleSelectTemplate = React.useCallback(
    async (template: EmailTemplate) => {
      const currentSubject = getSubject()
      const currentBody = getBody()
      let subject = template.subject || ''
      let body = template.body || ''
      let renderedSuccessfully = true

      try {
        const rendered = await mutation.mutateAsync(
          create(RenderEmailTemplateRequestSchema, {
            templateId: template.id,
            context: {
              organizationId,
              inquiryId,
              contactId,
              bookingId:
                parentType === ParentTypeEnum.PARENT_TYPE_BOOKING
                  ? parentId
                  : '',
              quoteId: parentType === ParentTypeEnum.PARENT_TYPE_QUOTE ? parentId : '',
              invoiceId: parentType === ParentTypeEnum.PARENT_TYPE_INVOICE ? parentId : '',
              paymentId: parentType === ParentTypeEnum.PARENT_TYPE_PAYMENT ? parentId : '',
            },
          }),
        )
        if (rendered.unresolvedVariables.length > 0) {
          renderedSuccessfully = false
          toast.warning('Template inserted without rendered variables', {
            description: rendered.unresolvedVariables.join(', '),
          })
        } else {
          subject = rendered.subject
          body = rendered.body
        }
      } catch (error) {
        renderedSuccessfully = false
        reportError(error, {
          feature: 'email_template_render',
          kind: 'mutation',
        })
        captureError(
          error instanceof Error ? error : new Error(String(error)),
          {
            context: 'email-compose-sheet-template-render',
          },
        )
        toast.error('Template inserted without rendered variables', {
          description: getErrorDescription(error),
        })
      }

      if (!currentSubject.trim() && subject) setSubject(subject)
      if (isHtmlEmpty(currentBody)) {
        replaceBody(body)
      } else {
        appendBody(body)
      }

      if (renderedSuccessfully) toast.success('Template inserted')
      markEdited()
    },
    [
      appendBody,
      contactId,
      getBody,
      getSubject,
      inquiryId,
      markEdited,
      mutation,
      organizationId,
      parentId,
      parentType,
      replaceBody,
      setSubject,
    ],
  )

  return {
    handleSelectTemplate,
    isRendering: mutation.isPending,
  }
}
